import React, { useCallback, useState, useEffect } from 'react';
import { useUI } from '../../contexts';

export type GlobalSearchResult = {
  filePath: string;
  fileName: string;
  line: number;
  column: number;
  preview: string;
  matchStart: number;
  matchEnd: number;
};

type GlobalSearchPanelProps = {
  groupedResults: Record<string, GlobalSearchResult[]>;
  loading: boolean;
  totalResults: number;
  mode: 'search' | 'replace';
  onSelectResult: (filePath: string, line: number, column: number) => void;
  onReplace: (filePath: string, line: number, searchText: string, replaceText: string) => void;
  onReplaceAll: (searchText: string, replaceText: string) => void;
  onClose: () => void;
};

export const GlobalSearchPanel = React.memo(function GlobalSearchPanel({
  groupedResults,
  loading,
  totalResults,
  mode,
  onSelectResult,
  onReplace,
  onReplaceAll,
  onClose,
}: GlobalSearchPanelProps) {
  const {
    globalSearchQuery,
    setGlobalSearchQuery,
    globalSearchInputRef,
  } = useUI();

  const [highlightIndex, setHighlightIndex] = useState(0);
  const [expandedFiles, setExpandedFiles] = useState<Set<string>>(new Set());
  const [replaceText, setReplaceText] = useState('');
  const [replacedItems, setReplacedItems] = useState<Set<string>>(new Set());
  const [isReplacing, setIsReplacing] = useState(false);
  const replaceInputRef = React.useRef<HTMLInputElement>(null);

  // Flatten results for keyboard navigation
  const flatResults = React.useMemo(() => {
    const results: GlobalSearchResult[] = [];
    Object.values(groupedResults).forEach((fileResults) => {
      results.push(...fileResults);
    });
    return results;
  }, [groupedResults]);

  // Auto-expand files when results change
  useEffect(() => {
    const files = Object.keys(groupedResults);
    setExpandedFiles(new Set(files.slice(0, 10))); // Expand first 10 files
    setHighlightIndex(0);
    setReplacedItems(new Set()); // Reset replaced items when search changes
  }, [groupedResults]);

  // Focus replace input when switching to replace mode
  useEffect(() => {
    if (mode === 'replace' && replaceInputRef.current) {
      // Delay focus to allow animation
      setTimeout(() => replaceInputRef.current?.focus(), 100);
    }
  }, [mode]);

  // Handle single replacement
  const handleReplaceSingle = useCallback((result: GlobalSearchResult) => {
    const key = `${result.filePath}:${result.line}:${result.column}`;
    if (replacedItems.has(key)) return;

    onReplace(result.filePath, result.line, globalSearchQuery, replaceText);
    setReplacedItems(prev => new Set([...Array.from(prev), key]));
  }, [globalSearchQuery, replaceText, replacedItems, onReplace]);

  // Handle replace all
  const handleReplaceAll = useCallback(async () => {
    if (!globalSearchQuery || isReplacing) return;
    setIsReplacing(true);

    try {
      await onReplaceAll(globalSearchQuery, replaceText);
      // Mark all as replaced
      const allKeys = flatResults.map(r => `${r.filePath}:${r.line}:${r.column}`);
      setReplacedItems(new Set(allKeys));
    } finally {
      setIsReplacing(false);
    }
  }, [globalSearchQuery, replaceText, flatResults, isReplacing, onReplaceAll]);

  // Replace in single file
  const handleReplaceInFile = useCallback((filePath: string) => {
    const fileResults = groupedResults[filePath] || [];
    fileResults.forEach(result => {
      const key = `${result.filePath}:${result.line}:${result.column}`;
      if (!replacedItems.has(key)) {
        onReplace(result.filePath, result.line, globalSearchQuery, replaceText);
        setReplacedItems(prev => new Set([...Array.from(prev), key]));
      }
    });
  }, [groupedResults, globalSearchQuery, replaceText, replacedItems, onReplace]);

  const toggleFileExpanded = useCallback((filePath: string) => {
    setExpandedFiles((prev) => {
      const next = new Set(prev);
      if (next.has(filePath)) {
        next.delete(filePath);
      } else {
        next.add(filePath);
      }
      return next;
    });
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightIndex((prev) => Math.min(prev + 1, flatResults.length - 1));
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightIndex((prev) => Math.max(prev - 1, 0));
        return;
      }

      if (e.key === 'Enter' && flatResults.length > 0) {
        e.preventDefault();
        const result = flatResults[highlightIndex];
        if (result) {
          onSelectResult(result.filePath, result.line, result.column);
          onClose();
        }
        return;
      }
    },
    [flatResults, highlightIndex, onClose, onSelectResult]
  );

  const renderHighlightedPreview = (result: GlobalSearchResult) => {
    const { preview, matchStart, matchEnd } = result;
    const before = preview.slice(0, matchStart);
    const match = preview.slice(matchStart, matchEnd);
    const after = preview.slice(matchEnd);

    return (
      <span className="global-search-preview">
        {before}
        <mark className="global-search-match">{match}</mark>
        {after}
      </span>
    );
  };

  return (
    <div className={`global-search-panel ${mode === 'replace' ? 'replace-mode' : ''}`} onKeyDown={handleKeyDown}>
      <div className="global-search-header">
        <div className="global-search-inputs">
          <div className="global-search-input-wrapper">
            <svg className="global-search-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398h-.001c.03.04.062.078.098.115l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85a1.007 1.007 0 0 0-.115-.1zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z" />
            </svg>
            <input
              ref={globalSearchInputRef}
              type="text"
              className="global-search-input"
              placeholder="Search in all files..."
              value={globalSearchQuery}
              onChange={(e) => setGlobalSearchQuery(e.target.value)}
              autoFocus
            />
            {globalSearchQuery && (
              <button
                className="global-search-clear"
                onClick={() => setGlobalSearchQuery('')}
                title="Clear search"
              >
                ×
              </button>
            )}
          </div>
          {mode === 'replace' && (
            <div className="global-replace-input-wrapper">
              <svg className="global-search-icon replace-icon" width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M2.5 3.5a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-11zm0 4a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-11zm0 4a.5.5 0 0 1 0-1h11a.5.5 0 0 1 0 1h-11z"/>
              </svg>
              <input
                ref={replaceInputRef}
                type="text"
                className="global-replace-input"
                placeholder="Replace with..."
                value={replaceText}
                onChange={(e) => setReplaceText(e.target.value)}
              />
              <div className="replace-actions">
                <button
                  className="replace-action-btn"
                  onClick={handleReplaceAll}
                  disabled={!globalSearchQuery || totalResults === 0 || isReplacing}
                  title="Replace All (Cmd+Shift+Enter)"
                >
                  {isReplacing ? '...' : 'Replace All'}
                </button>
              </div>
            </div>
          )}
        </div>
        <button className="global-search-close" onClick={onClose} title="Close (Esc)">
          ×
        </button>
      </div>

      <div className="global-search-status">
        {loading ? (
          <span className="global-search-loading">Searching...</span>
        ) : globalSearchQuery.length < 2 ? (
          <span className="global-search-hint">Type at least 2 characters to search</span>
        ) : totalResults === 0 ? (
          <span className="global-search-no-results">No results found</span>
        ) : (
          <span className="global-search-count">
            {totalResults} result{totalResults !== 1 ? 's' : ''} in {Object.keys(groupedResults).length} file
            {Object.keys(groupedResults).length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      <div className="global-search-results">
        {Object.entries(groupedResults).map(([filePath, results]) => {
          const isExpanded = expandedFiles.has(filePath);
          const fileName = filePath.split('/').pop() || filePath;
          const dirPath = filePath.slice(0, filePath.length - fileName.length - 1) || '/';

          return (
            <div key={filePath} className="global-search-file-group">
              <div className="global-search-file-header">
                <button
                  className="file-header-main"
                  onClick={() => toggleFileExpanded(filePath)}
                >
                  <span className={`global-search-expand-icon ${isExpanded ? 'expanded' : ''}`}>
                    ▶
                  </span>
                  <span className="global-search-file-name">{fileName}</span>
                  <span className="global-search-file-path">{dirPath}</span>
                  <span className="global-search-file-count">{results.length}</span>
                </button>
                {mode === 'replace' && (
                  <button
                    className="replace-file-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReplaceInFile(filePath);
                    }}
                    title="Replace all in this file"
                  >
                    ↻ All
                  </button>
                )}
              </div>

              {isExpanded && (
                <div className="global-search-file-results">
                  {results.map((result, index) => {
                    const globalIndex = flatResults.findIndex(
                      (r) =>
                        r.filePath === result.filePath &&
                        r.line === result.line &&
                        r.column === result.column
                    );
                    const isHighlighted = globalIndex === highlightIndex;
                    const key = `${result.filePath}:${result.line}:${result.column}`;
                    const isReplaced = replacedItems.has(key);

                    return (
                      <div
                        key={`${result.line}-${result.column}-${index}`}
                        className={`global-search-result-item ${isHighlighted ? 'highlighted' : ''} ${isReplaced ? 'replaced' : ''}`}
                      >
                        <button
                          className="result-content"
                          onClick={() => {
                            onSelectResult(result.filePath, result.line, result.column);
                            onClose();
                          }}
                        >
                          <span className="global-search-line-num">{result.line}</span>
                          {renderHighlightedPreview(result)}
                        </button>
                        {mode === 'replace' && !isReplaced && (
                          <button
                            className="replace-single-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReplaceSingle(result);
                            }}
                            title="Replace"
                          >
                            ↻
                          </button>
                        )}
                        {isReplaced && (
                          <span className="replaced-badge">✓</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
});
